import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Camera, Save, MapPin, Shield, Smartphone, KeyRound } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import ChangePasswordDialog from '../components/ChangePasswordDialog';

const UserSettingsPage = () => {
  const { user, checkAuth } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [homeAddress, setHomeAddress] = useState('');
  const [profilePicUrl, setProfilePicUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // 2FA
  const [twofa, setTwofa] = useState({ enabled: false, remaining_backup_codes: 0 });
  const [twofaStep, setTwofaStep] = useState(null); // 'qr' | 'code' | 'backup'
  const [enrolData, setEnrolData] = useState(null);
  const [enrolCode, setEnrolCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');

  // Change password
  const [showChangePwd, setShowChangePwd] = useState(false);

  // Reminder preferences
  const [reminderPrefs, setReminderPrefs] = useState({ subscriptions: {} });
  const [calendarItems, setCalendarItems] = useState([]);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const loadTwofa = async () => {
    try { const r = await api.get('/auth/2fa/status'); setTwofa(r.data); } catch {}
  };
  const loadReminders = async () => {
    try {
      const [prefs, cal] = await Promise.all([
        api.get('/me/reminder-preferences'),
        api.get('/reporting-calendar'),
      ]);
      setReminderPrefs(prefs.data);
      setCalendarItems(cal.data);
    } catch {}
  };
  useEffect(() => { loadTwofa(); loadReminders(); }, []);

  const togglePref = (key) => {
    setReminderPrefs(prev => ({ ...prev, subscriptions: { ...(prev.subscriptions || {}), [key]: !(prev.subscriptions || {})[key] } }));
  };
  const savePrefs = async () => {
    setSavingPrefs(true);
    try { await api.put('/me/reminder-preferences', { subscriptions: reminderPrefs.subscriptions || {} }); toast.success('Reminder preferences saved'); }
    catch { toast.error('Failed to save'); }
    setSavingPrefs(false);
  };

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setHomeAddress(user.home_address || '');
      setProfilePicUrl(user.profile_picture_url || '');
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/auth/profile', {
        first_name: firstName,
        last_name: lastName,
        home_address: homeAddress,
        profile_picture_url: profilePicUrl,
      });
      toast.success('Profile updated successfully');
      await checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update profile');
    }
    setSaving(false);
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        setProfilePicUrl(base64);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Failed to upload picture');
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 fade-in max-w-2xl" data-testid="user-settings-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Dashboard</span><span>/</span><span className="text-[#1f1f1f] font-medium">User Settings</span>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">PROFILE</p>
        <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">User Settings</h1>
      </div>

      {/* Profile Picture */}
      <div className="bg-white border border-gray-200 p-6" data-testid="profile-picture-section">
        <div className="flex items-center gap-2 mb-4">
          <Camera className="w-5 h-5 text-[#C5A059]" />
          <h3 className="font-semibold text-[#1f1f1f]">Profile Picture</h3>
        </div>
        <div className="flex items-center gap-6">
          <div className="relative">
            {profilePicUrl ? (
              <img
                src={profilePicUrl}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                data-testid="profile-picture-preview"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#1f1f1f]/10 flex items-center justify-center" data-testid="profile-picture-placeholder">
                <User className="w-10 h-10 text-[#1f1f1f]/40" />
              </div>
            )}
            <label className="absolute bottom-0 right-0 bg-[#1f1f1f] text-white w-8 h-8 rounded-full flex items-center justify-center cursor-pointer hover:bg-[#333333] transition-colors">
              <Camera className="w-4 h-4" />
              <input
                type="file"
                accept="image/*"
                onChange={handlePictureUpload}
                className="hidden"
                data-testid="profile-picture-upload"
              />
            </label>
          </div>
          <div>
            <p className="text-sm text-gray-600">Upload a profile picture</p>
            <p className="text-xs text-gray-400 mt-1">JPG, PNG under 2MB. Will appear in the navigation bar.</p>
            {uploading && <p className="text-xs text-[#C5A059] mt-1">Uploading...</p>}
          </div>
        </div>
      </div>

      {/* Personal Details */}
      <div className="bg-white border border-gray-200 p-6" data-testid="personal-details-section">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-[#C5A059]" />
          <h3 className="font-semibold text-[#1f1f1f]">Personal Details</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">Your name will appear in the navigation bar and on documents you create.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">First Name</Label>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter first name"
              data-testid="first-name-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Last Name</Label>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter last name"
              data-testid="last-name-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-gray-500">Email</Label>
            <Input value={user?.email || ''} disabled className="bg-gray-50" data-testid="email-display" />
            <p className="text-[10px] text-gray-400 mt-1">Email cannot be changed</p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-gray-500">Role</Label>
            <Input value={user?.role || ''} disabled className="bg-gray-50 capitalize" data-testid="role-display" />
          </div>
        </div>
      </div>

      {/* Home Address */}
      <div className="bg-white border border-gray-200 p-6" data-testid="address-section">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-[#C5A059]" />
          <h3 className="font-semibold text-[#1f1f1f]">Home Address</h3>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500">Address</Label>
          <Input
            value={homeAddress}
            onChange={(e) => setHomeAddress(e.target.value)}
            placeholder="123 Main Street, Johannesburg, 2000"
            data-testid="home-address-input"
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#1f1f1f] hover:bg-[#333333] text-white"
          data-testid="save-profile-btn"
        >
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Change Password */}
      <div className="bg-white border border-gray-200 p-6" data-testid="change-password-section">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-5 h-5 text-[#C5A059]" />
          <h3 className="font-semibold text-[#1f1f1f]">Password</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Change the password you use to sign in to the CBS console. You'll need your current password.
        </p>
        <Button onClick={() => setShowChangePwd(true)}
                className="bg-[#1f1f1f] hover:bg-[#333333] text-white"
                data-testid="open-change-password-btn">
          <KeyRound className="w-4 h-4 mr-2" />Change password
        </Button>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white border border-gray-200 p-6" data-testid="twofa-section">
        <div className="flex items-center gap-2 mb-4">
          <Shield className={`w-5 h-5 ${twofa.enabled ? 'text-emerald-600' : 'text-gray-400'}`} />
          <h3 className="font-semibold text-[#1f1f1f]">Two-Factor Authentication</h3>
          {twofa.enabled && <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded">ENABLED</span>}
        </div>

        {!twofa.enabled && !twofaStep && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Add an extra layer of security to your account by requiring a 6-digit code from your authenticator app on every login. Compatible with Google Authenticator, Microsoft Authenticator, Authy, and 1Password.</p>
            <Button onClick={async () => {
              try {
                const r = await api.post('/auth/2fa/setup');
                setEnrolData(r.data);
                setTwofaStep('qr');
              } catch { toast.error('Failed to start 2FA setup'); }
            }} className="bg-[#1f1f1f] text-white" data-testid="enable-2fa-btn">
              <Smartphone className="w-4 h-4 mr-2" />Enable Two-Factor Authentication
            </Button>
          </div>
        )}

        {!twofa.enabled && twofaStep === 'qr' && enrolData && (
          <div className="space-y-3">
            <p className="text-xs text-gray-600">1. Scan this QR code with your authenticator app:</p>
            <div className="flex justify-center bg-gray-50 p-4 border"><img src={enrolData.qr_code} alt="QR" className="w-48 h-48" data-testid="user-2fa-qr" /></div>
            <details className="text-xs text-gray-500"><summary className="cursor-pointer">Can't scan? Show secret key</summary><p className="mt-2 font-mono bg-gray-50 p-2 break-all">{enrolData.secret}</p></details>
            <p className="text-xs text-gray-600">2. Enter the 6-digit code your app shows:</p>
            <div className="flex gap-2">
              <Input value={enrolCode} onChange={e => setEnrolCode(e.target.value)} placeholder="123456" maxLength={6} className="font-mono text-center tracking-widest" data-testid="user-2fa-confirm-code" />
              <Button onClick={async () => {
                try {
                  const r = await api.post('/auth/2fa/enable', { code: enrolCode });
                  setEnrolData({ ...enrolData, backup_codes: r.data.backup_codes });
                  setTwofaStep('backup');
                  setEnrolCode('');
                  toast.success('2FA enabled!');
                  loadTwofa();
                } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
              }} className="bg-[#1f1f1f] text-white" data-testid="user-2fa-confirm-btn">Confirm</Button>
            </div>
          </div>
        )}

        {twofaStep === 'backup' && enrolData?.backup_codes && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
              ✓ 2FA is now enabled. <strong>Save these backup codes</strong> — each one can be used once if you lose your authenticator device. They will not be shown again.
            </div>
            <div className="grid grid-cols-2 gap-2 bg-gray-50 border p-3 font-mono text-sm">
              {enrolData.backup_codes.map((c, i) => (<div key={i} className="px-2 py-1 bg-white border text-center" data-testid={`user-backup-code-${i}`}>{c}</div>))}
            </div>
            <Button onClick={() => { setTwofaStep(null); setEnrolData(null); }} variant="outline" data-testid="user-2fa-done-btn">I have saved them</Button>
          </div>
        )}

        {twofa.enabled && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Two-factor authentication is active. You have <strong>{twofa.remaining_backup_codes}</strong> backup codes remaining.</p>
            <div className="flex gap-2">
              <Input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)} placeholder="Enter your password to disable" className="max-w-xs" data-testid="disable-2fa-password" />
              <Button onClick={async () => {
                if (!window.confirm('Disable 2FA? Your account security will be reduced.')) return;
                try {
                  await api.post('/auth/2fa/disable', { password: disablePassword });
                  toast.success('2FA disabled');
                  setDisablePassword('');
                  loadTwofa();
                } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
              }} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="disable-2fa-btn">Disable 2FA</Button>
            </div>
          </div>
        )}
      </div>

      {/* Regulatory Reporting Reminders */}
      <div className="bg-white border border-gray-200 p-6" data-testid="reminder-prefs-section">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-[#1f1f1f]">Reporting Reminders</h3>
          {(['admin','manager','accountant','compliance_officer'].includes(user?.role)) ? null : (
            <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 bg-gray-100 text-gray-500 rounded">Permission may be required</span>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">Choose which regulatory reporting reminders you want to receive in-app. The system fires alerts at T-7 / T-3 / T-1 days before each due date (configurable by admin in <em>Reporting Calendar</em>).</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {calendarItems.map(item => (
            <label key={item.report_key} className="flex items-start gap-3 p-2 border border-gray-100 hover:bg-gray-50 cursor-pointer" data-testid={`pref-${item.report_key}`}>
              <input type="checkbox" checked={!!(reminderPrefs.subscriptions || {})[item.report_key]} onChange={() => togglePref(item.report_key)} className="mt-1" />
              <div className="flex-1 text-xs">
                <div className="font-medium text-[#1f1f1f]">{item.name}</div>
                <div className="text-gray-400">{item.frequency} · next due {item.next_due_date || '—'}</div>
              </div>
            </label>
          ))}
          {calendarItems.length === 0 && <p className="text-gray-400 text-xs col-span-full text-center py-4">No reporting calendar entries — ask an admin to seed the calendar.</p>}
        </div>
        <Button onClick={savePrefs} disabled={savingPrefs} className="bg-[#1f1f1f] text-white mt-4" data-testid="save-prefs-btn">
          <Save className="w-4 h-4 mr-2" />{savingPrefs ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>

      {/* Change password dialog */}
      <ChangePasswordDialog
        open={showChangePwd}
        onClose={() => setShowChangePwd(false)}
        onSuccess={() => { if (checkAuth) checkAuth(); }}
      />
    </div>
  );
};

export default UserSettingsPage;
